import { useRef, useState, useEffect, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import { classNames } from '../../../utils/classNames';
import { useChatboxMutation, useUploadChatFileMutation } from '../../../store/chatbox/chatboxApi';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setChatId, setChatMessages } from '../../../store/slices/chats.slice';
import { useTheme } from '../../../utils/theme-context';
import S3ExcelPreview from '../s3-preview/S3ExcelPreview';
import S3PdfPreview from '../s3-preview/S3PdfPreview';
import CameraModal from './modal/CameraModal';

type SpeechRecognition = any
type SpeechRecognitionEvent = any

const ChatInput = ({
	onSend
}: {
	onSend?: () => void
}) => {
	const [show, setShow] = useState<boolean>();
	const { id: tripId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const action = queryParams.get('type');
	const feedbackId = queryParams.get('feedbackId');
	const cabinId = queryParams.get('cabinId');
	const [files, setFiles] = useState<{ name: string, url: string }[]>([])
	const { theme } = useTheme();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();
	const newChat = searchParams.get('newChat');
	const [isListening, setIsListening] = useState(false);
	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [chatBoxAdd, { isLoading }] = useChatboxMutation()
	const [chatboxFile] = useUploadChatFileMutation();
	const cabinStageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const latestStageRef = useRef<string | undefined>(undefined);

	const { register, handleSubmit, reset, setFocus, setValue } = useForm<{ message: string }>();
	const { searchBar } = useSelector(
		(state: RootState) => state.features
	);
	const { topicId, messages } = useSelector(
		(state: RootState) => state.chats
	);
	const latestStage = [...(messages || [])].reverse().find((message: any) => message?.stage)?.stage;
	const messageRegister = register('message');

	useEffect(() => {
		latestStageRef.current = latestStage;
	}, [latestStage]);

	useEffect(() => {
		if (searchParams.get('topicId')) {
			dispatch(setChatId(searchParams.get('topicId')))
		}
	}, [searchParams.get('topicId')])

	useEffect(() => {
		const SpeechRecognition =
			(window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (SpeechRecognition) {
			const recognition = new SpeechRecognition();
			recognition.lang = 'en-US';
			recognition.interimResults = false;
			recognition.maxAlternatives = 1;

			recognition.onresult = (event: SpeechRecognitionEvent) => {
				const transcript = event.results[0][0].transcript;
				if (inputRef.current) {
					inputRef.current.value = transcript;
				}
				setValue('message', transcript);
				if (latestStageRef.current === 'require_cabin') {
					if (cabinStageTimerRef.current) clearTimeout(cabinStageTimerRef.current);
					cabinStageTimerRef.current = setTimeout(() => {
						navigate('/staff/home');
					}, 10000);
				}
			};

			recognition.onend = () => {
				setIsListening(false);
			};

			recognition.onerror = (err: any) => {
				console.error('Speech recognition error', err);
				setIsListening(false);
			};

			recognitionRef.current = recognition;
		} else {
			console.error('Speech recognition not supported in this browser');
		}
	}, [navigate, setValue]);

	useEffect(() => {
		if (cabinStageTimerRef.current) {
			clearTimeout(cabinStageTimerRef.current);
			cabinStageTimerRef.current = null;
		}

		if (latestStage === 'require_cabin') {
			cabinStageTimerRef.current = setTimeout(() => {
				navigate('/staff/home');
			}, 10000);
		}

		return () => {
			if (cabinStageTimerRef.current) {
				clearTimeout(cabinStageTimerRef.current);
				cabinStageTimerRef.current = null;
			}
		};
	}, [latestStage, navigate]);

	const handleRequireCabinActivity = () => {
		if (latestStage !== 'require_cabin') return;
		if (cabinStageTimerRef.current) clearTimeout(cabinStageTimerRef.current);
		cabinStageTimerRef.current = setTimeout(() => {
			navigate('/staff/home');
		}, 10000);
	};

	const onSubmit = async ({ message }: { message: string }) => {
		// const user = { name: userInfo?.data.name || '', email: userInfo?.data.email || '', id: userInfo?.data.id || '' }
		try {
			handleRequireCabinActivity();
			reset({ message: '' })
			setFiles([]);
			setTimeout(() => {
				document.querySelector('#chatEnd')?.scrollIntoView({ behavior: 'smooth' });
			}, 300)
			if (topicId) {
				await chatBoxAdd(
					files.length ? { tripId: tripId || '', action: action || undefined, cabinId: cabinId || undefined, topic: topicId, feedbackId: feedbackId || undefined, message, files } : { tripId: tripId || '', action: action || undefined, cabinId: cabinId || undefined, topic: topicId, feedbackId: feedbackId || undefined, message }
				).unwrap();
			} else {
				await chatBoxAdd(
					files.length ? { tripId: tripId || '', action: action || undefined, cabinId: cabinId || undefined, feedbackId: feedbackId || undefined, message, files } : { tripId: tripId || '', action: action || undefined, cabinId: cabinId || undefined, feedbackId: feedbackId || undefined, message }
				).unwrap();
			}

		} catch (error) {
		} finally {
			// setTimeout(() => {
			//     contentEditableRef.current?.focus();
			// }, 100);
		}
	}

	const onInputClick = () => {
		onSend && onSend()
	}

	const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
		if (evt.key === 'Enter' && !evt.shiftKey) {
			evt.preventDefault();
			handleSubmit(onSubmit)();
		}

	};

	useEffect(() => {
		if (newChat) {
			onSend && onSend()
			dispatch(setChatMessages([]))
			dispatch(setChatId(''))
		}
		setTimeout(() => {
			setFocus('message')
		}, 100)
		return () => {
			dispatch(setChatId(''))
		};
	}, [newChat])

	const handleMicClick = () => {
		if (isListening || !recognitionRef.current) return;
		setIsListening(true);
		recognitionRef.current.start();
	};

	const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			try {
				reset({ message: '' })
				onSend && onSend()
				const formData = new FormData();
				formData.append('files', file);
				const newFile = await chatboxFile(formData).unwrap();
				setFiles(files ? [...files, ...newFile] : newFile);
				// setFileUrls();
			} catch (error) {
			}
		}
	};

	const handleRemoveFile = (name: string) => {
		const tempFile = [...files].filter(file => file.name !== name)
		setFiles(tempFile)
	}

	const handleAddFile = (newFile: {
		name: string;
		url: string;
	}[]) => setFiles(files ? [...files, ...newFile] : newFile);


	const handleCapture = () => setShow(show => !show);

	return (
		<form className={classNames('flex flex-wrap max-w-full xl:w-[760px] relative mx-auto', searchBar ? '' : 'opacity-10')} onSubmit={handleSubmit(onSubmit)}>
			{show && <CameraModal open={show} onClose={handleCapture} setAddFile={handleAddFile} />}
			{files && files.map(file =>
				<div key={file.name} className='m-2 relative'>
					<button onClick={() => handleRemoveFile(file.name)} className='absolute -top-2 -right-2 bg-white rounded-full' type='button'>
						<img alt='close' width={16} height={16} className='' src='/svg/icons/close-modal.svg' />
					</button>
					{(file.name.includes('.pdf')) ?
						<div className='overflow-auto w-20 h-20 border border-gray-200'>
							<S3PdfPreview pdfUrl={file.url} />
						</div> :
						file.name.includes('.xlsx') ? <div className='overflow-auto w-20 h-20 border border-gray-200'>
							<S3ExcelPreview s3Url={file.url} />
						</div> :
							<div className='w-20 h-20 bg-gray-100 border border-gray-200'>
								<img src={file.url} className='w-full h-full object-cover' alt={file.name} />
							</div>
					}
				</div>
			)}
			{!searchBar && <div className='absolute top-0 left-0 w-full h-full opacity-10 z-10' />}
			<div className='relative w-full'>
				<button
					type="button"
					onClick={handleMicClick}
					disabled={isListening}
					className={classNames(
						'absolute left-3 top-1/2 transform -translate-y-1/2 w-7 h-7',
						isListening ? 'animate-pulse' : ''
					)}
				>
					<img src={theme === 'dark' ? '/svg/icons/microphone-white.svg' : '/svg/icons/microphone.svg'} alt='Mic' />
				</button>
				<input
					{...messageRegister}
					autoComplete='off'
					onKeyDown={handleKeyDown}
					onClick={onInputClick}
					onChange={(e) => {
						messageRegister.onChange(e);
						handleRequireCabinActivity();
					}}
					placeholder='Capture some feedback...'
					className='dark:bg-black dark:text-primary-darkMode border-2 border-primary-default outline-none dark:border-primary-darkMode rounded-2xl py-3 px-4 pl-10 w-full text-sm pr-24'
					type="text"
				/>
				<label
					htmlFor='file-upload'
					className='absolute right-10 top-2.5 cursor-pointer'
				>
					<img src={theme === 'dark' ? '/svg/icons/file-upload-white.svg' : '/svg/icons/file-upload.svg'} className='object-contain w-[25px]' />
					<input
						id="file-upload"
						type="file"
						className='hidden'
						accept="image/*,video/*"
						capture='environment'
						onChange={handleFileUpload}
					/>
				</label>
				<button
					type='button'
					className='absolute right-16 top-3 cursor-pointer mr-1'
					onClick={handleCapture}
				>
					<img src={theme === 'dark' ? '/svg/icons/capture-white.svg' : '/svg/icons/capture.svg'} className='object-contain w-6 h-[22px]' />
				</button>
				<button className='absolute right-3 top-2.5' disabled={isLoading} type='submit'>
					<img src={theme === 'dark' ? '/svg/icons/send-white.svg' : '/svg/icons/send.svg'} className='object-contain w-[25px]' />
				</button>
			</div>
		</form>
	)
}

export default ChatInput